/* eslint-disable max-lines -- canonical virtual-tour demo fixtures span many nodes, markers, and links */
import { MOCK_AAE_FACTORY_ID } from '../../factory/mock/aae-factory.mock';
import type { PsvLink } from '../psv-link';
import type { PsvMarker } from '../psv-marker';
import type { PsvNode } from '../psv-node';
import type { VirtualTourNode } from '../virtual-tour-node';

/**
 * Public Photo Sphere Viewer sample panoramas, reused so the demo build
 * and mocked e2e suite render a real walkthrough without bundling large
 * equirectangular images into the repo.
 */
const DEMO_PANORAMAS = [
  'https://photo-sphere-viewer-data.netlify.app/assets/tour/key-biscayne-1.jpg',
  'https://photo-sphere-viewer-data.netlify.app/assets/tour/key-biscayne-2.jpg',
  'https://photo-sphere-viewer-data.netlify.app/assets/tour/key-biscayne-3.jpg',
] as const;

/** Canonical mock {@link PsvNode}. */
export const MOCK_PSV_NODE: PsvNode = {
  id: 1,
  factory_id: 1,
  name: 'Entrance',
  panorama_url: DEMO_PANORAMAS[0],
};

/** Canonical mock {@link PsvLink} (jump from the parent node to node 2). */
export const MOCK_PSV_LINK: PsvLink = {
  id: 1,
  name: 'Workshop',
  node_id: 2,
  yaw: 40,
  pitch: 0,
};

/** Canonical mock {@link PsvMarker}. */
export const MOCK_PSV_MARKER: PsvMarker = {
  id: 1,
  name: 'Reception',
  description: 'Visitor check-in and safety briefing',
  machine_park_id: null,
  yaw: 10,
  pitch: -5,
};

/** Builds a {@link PsvNode} from {@link MOCK_PSV_NODE}. */
export function createMockPsvNode(overrides?: Partial<PsvNode>): PsvNode {
  return { ...MOCK_PSV_NODE, ...overrides };
}

/** Builds a {@link PsvLink} from {@link MOCK_PSV_LINK}. */
export function createMockPsvLink(overrides?: Partial<PsvLink>): PsvLink {
  return { ...MOCK_PSV_LINK, ...overrides };
}

/** Builds a {@link PsvMarker} from {@link MOCK_PSV_MARKER}. */
export function createMockPsvMarker(overrides?: Partial<PsvMarker>): PsvMarker {
  return { ...MOCK_PSV_MARKER, ...overrides };
}

// ─── Advanced Armour Engineering (AAE) virtual tour data ────────────────────
// Migrated from apps_old/indutrust-front/src/app/components/vr-psv/AAE-vr.ts.
// Panoramas are served from the Indutrust production CDN. Yaw/pitch values
// were converted from radians (legacy PSV format) to degrees (× 180/π).
// Bound to {@link MOCK_AAE_FACTORY_ID} (map pin 43), not showcase factory 1.
//
// Node ID mapping (legacy string → numeric):
// entry-01=1, wh1-01=2, wh1-02=3, wh1-04=4, wh1-03=5,
// wh1-05=6, wh1-06=7, wh1-07=8, wh1-08=9, wh1-09=10,
// lab-03=11, lab-02=12, lab-01=13, entry-02=14,
// wh3-01=15, wh3-02=16, wh3-05=17, wh4-01=18, wh4-02=19, wh4-03=20
const AAE_F = MOCK_AAE_FACTORY_ID;

/** Production CDN base for Advanced Armour Equipment panorama assets. */
export const MOCK_AAE_CDN = 'https://indutrust-api-production.herokuapp.com/vt';

/** Legacy AAE panorama filenames keyed by numeric node id (1–20). */
const AAE_PANORAMA_FILES: Record<number, string> = {
  1: 'entry-01-2.JPG',
  2: 'wh1-01-1.JPG',
  3: 'wh1-02-1.JPG',
  4: 'wh1-04-1.JPG',
  5: 'wh1-03-2.JPG',
  6: 'wh1-05-2.JPG',
  7: 'wh1-06-2.JPG',
  8: 'wh1-07-1.JPG',
  9: 'wh1-08-2.JPG',
  10: 'wh1-09-1.JPG',
  11: 'lab-03-1.JPG',
  12: 'lab-02-1.JPG',
  13: 'lab-01-1.JPG',
  14: 'entry-02-1.JPG',
  15: 'wh3-01-2.JPG',
  16: 'wh3-02-2.JPG',
  17: 'wh3-05-1.JPG',
  18: 'wh4-01-2.JPG',
  19: 'wh4-02-2.JPG',
  20: 'wh4-03-2.JPG',
};

const aaePanoramaUrl = (nodeId: number): string =>
  `${MOCK_AAE_CDN}/${AAE_PANORAMA_FILES[nodeId]}`;

const AAE_TOUR_NODES: VirtualTourNode[] = [
  {
    id: 1,
    factory_id: AAE_F,
    name: 'Front Office Entry',
    panorama_url: aaePanoramaUrl(1),
    links: [
      {
        id: 1,
        name: 'Warehouse 1 - Entry',
        node_id: 2,
        yaw: 357.85,
        pitch: -0.04,
      },
      {
        id: 2,
        name: 'Warehouse 3 - Entry',
        node_id: 18,
        yaw: 97.91,
        pitch: 1.03,
      },
    ],
    markers: [],
  },
  {
    id: 2,
    factory_id: AAE_F,
    name: 'Warehouse 1 - Entry',
    panorama_url: aaePanoramaUrl(2),
    links: [
      {
        id: 3,
        name: 'Front Office Entry',
        node_id: 1,
        yaw: 108.09,
        pitch: -9.94,
      },
      {
        id: 4,
        name: 'Warehouse 1 - Bending Area',
        node_id: 3,
        yaw: 301.09,
        pitch: -0.45,
      },
      {
        id: 5,
        name: 'Warehouse 1 - Stocking Area (1)',
        node_id: 4,
        yaw: 338.11,
        pitch: 1.67,
      },
      {
        id: 6,
        name: 'Warehouse 1 - X Area',
        node_id: 6,
        yaw: 74.8,
        pitch: -4.78,
      },
    ],
    markers: [
      {
        id: 1,
        name: 'Penta Laser 7000',
        description: [
          'Fiber laser cutting cell for plate work in Warehouse 1.',
          'Bed: 6 m × 2.5 m',
          'Source: 6 kW fiber',
          'Process: 2D laser cutting',
          'Brand: Penta',
        ].join('\n'),
        machine_park_id: 103,
        yaw: 44.07,
        pitch: 7.95,
      },
    ],
  },
  {
    id: 3,
    factory_id: AAE_F,
    name: 'Warehouse 1 - Bending Area',
    panorama_url: aaePanoramaUrl(3),
    links: [
      {
        id: 7,
        name: 'Warehouse 1 - Entry',
        node_id: 2,
        yaw: 115.53,
        pitch: -12.6,
      },
      {
        id: 8,
        name: 'Warehouse 1 - Stocking Area (1)',
        node_id: 4,
        yaw: 27.71,
        pitch: -11.29,
      },
    ],
    markers: [
      {
        id: 2,
        name: 'TruBend 5320',
        description: null,
        machine_park_id: 123,
        yaw: 204.84,
        pitch: 9.8,
      },
      {
        id: 3,
        name: 'Machine X',
        description: null,
        machine_park_id: null,
        yaw: 265.77,
        pitch: 9.19,
      },
      {
        id: 4,
        name: 'Machine X',
        description: null,
        machine_park_id: null,
        yaw: 84.19,
        pitch: 6.41,
      },
    ],
  },
  {
    id: 4,
    factory_id: AAE_F,
    name: 'Warehouse 1 - Stocking Area (1)',
    panorama_url: aaePanoramaUrl(4),
    links: [
      {
        id: 9,
        name: 'Warehouse 1 - Stocking Area (2)',
        node_id: 5,
        yaw: 286.1,
        pitch: -15.32,
      },
      {
        id: 10,
        name: 'Warehouse 1 - Bending Area',
        node_id: 3,
        yaw: 226.71,
        pitch: -1.42,
      },
      {
        id: 11,
        name: 'Warehouse 1 - Entry',
        node_id: 2,
        yaw: 186.38,
        pitch: -1.08,
      },
      {
        id: 12,
        name: 'Laboratory Entry',
        node_id: 11,
        yaw: 347.41,
        pitch: 2.53,
      },
    ],
    markers: [
      {
        id: 5,
        name: 'Machine X',
        description: null,
        machine_park_id: null,
        yaw: 65.33,
        pitch: 8.79,
      },
    ],
  },
  {
    id: 5,
    factory_id: AAE_F,
    name: 'Warehouse 1 - Stocking Area (2)',
    panorama_url: aaePanoramaUrl(5),
    links: [
      {
        id: 13,
        name: 'Warehouse 1 - Stocking Area (1)',
        node_id: 4,
        yaw: 78.68,
        pitch: -2.98,
      },
    ],
    markers: [],
  },
  {
    id: 6,
    factory_id: AAE_F,
    name: 'Warehouse 1 - X Area',
    panorama_url: aaePanoramaUrl(6),
    links: [
      {
        id: 14,
        name: 'Warehouse 1 - Entry',
        node_id: 2,
        yaw: 230.63,
        pitch: -1.93,
      },
      {
        id: 15,
        name: 'Warehouse 1 - X Area (2)',
        node_id: 7,
        yaw: 28,
        pitch: -0.91,
      },
      {
        id: 16,
        name: 'Warehouse 1 - Welding Area 3',
        node_id: 10,
        yaw: 104.84,
        pitch: -5.18,
      },
    ],
    markers: [
      {
        id: 6,
        name: 'Machine X',
        description: null,
        machine_park_id: null,
        yaw: 266.74,
        pitch: 6.75,
      },
      {
        id: 7,
        name: 'Machine X',
        description: null,
        machine_park_id: null,
        yaw: 314.26,
        pitch: 10.23,
      },
    ],
  },
  {
    id: 7,
    factory_id: AAE_F,
    name: 'Warehouse 1 - X Area (2)',
    panorama_url: aaePanoramaUrl(7),
    links: [
      {
        id: 17,
        name: 'Warehouse 1 - X Area',
        node_id: 6,
        yaw: 209.53,
        pitch: -2.3,
      },
      {
        id: 18,
        name: 'Warehouse 1 - Welding Area 1',
        node_id: 8,
        yaw: 72.87,
        pitch: -7.55,
      },
      {
        id: 19,
        name: 'Warehouse 1 - Welding Area 3',
        node_id: 10,
        yaw: 141.84,
        pitch: -0.95,
      },
    ],
    markers: [
      {
        id: 8,
        name: 'Machine X',
        description: null,
        machine_park_id: null,
        yaw: 266.74,
        pitch: 6.75,
      },
    ],
  },
  {
    id: 8,
    factory_id: AAE_F,
    name: 'Warehouse 1 - Welding Area 1',
    panorama_url: aaePanoramaUrl(8),
    links: [
      {
        id: 20,
        name: 'Warehouse 1 - X Area (2)',
        node_id: 7,
        yaw: 262.88,
        pitch: -9.12,
      },
      {
        id: 21,
        name: 'Warehouse 1 - Welding Area 2',
        node_id: 9,
        yaw: 90.7,
        pitch: -7.46,
      },
    ],
    markers: [],
  },
  {
    id: 9,
    factory_id: AAE_F,
    name: 'Warehouse 1 - Welding Area 2',
    panorama_url: aaePanoramaUrl(9),
    links: [
      {
        id: 22,
        name: 'Warehouse 1 - Welding Area 1',
        node_id: 8,
        yaw: 269.44,
        pitch: -11.65,
      },
      {
        id: 23,
        name: 'Laboratory Entry',
        node_id: 11,
        yaw: 20.31,
        pitch: 2.21,
      },
    ],
    markers: [],
  },
  {
    id: 10,
    factory_id: AAE_F,
    name: 'Warehouse 1 - Welding Area 3',
    panorama_url: aaePanoramaUrl(10),
    links: [
      {
        id: 24,
        name: 'Warehouse 1 - X Area',
        node_id: 6,
        yaw: 273.3,
        pitch: -8.61,
      },
    ],
    markers: [],
  },
  {
    id: 11,
    factory_id: AAE_F,
    name: 'Laboratory Entry',
    panorama_url: aaePanoramaUrl(11),
    links: [
      { id: 25, name: 'Laboratory - 1', node_id: 12, yaw: 3.44, pitch: 2.15 },
      {
        id: 26,
        name: 'Warehouse 2 - Entry',
        node_id: 14,
        yaw: 99.09,
        pitch: 0.25,
      },
      {
        id: 27,
        name: 'Warehouse 1 - Welding Area 2',
        node_id: 9,
        yaw: 164.95,
        pitch: 0.93,
      },
    ],
    markers: [],
  },
  {
    id: 12,
    factory_id: AAE_F,
    name: 'Laboratory - 1',
    panorama_url: aaePanoramaUrl(12),
    links: [
      {
        id: 28,
        name: 'Laboratory Entry',
        node_id: 11,
        yaw: 320.58,
        pitch: -0.42,
      },
      { id: 29, name: 'Laboratory - 2', node_id: 13, yaw: 78.22, pitch: -2.55 },
    ],
    markers: [
      {
        id: 9,
        name: 'Machine X',
        description: null,
        machine_park_id: null,
        yaw: 37.61,
        pitch: 10.99,
      },
      {
        id: 10,
        name: 'Machine X',
        description: null,
        machine_park_id: null,
        yaw: 140.31,
        pitch: 3.62,
      },
      {
        id: 11,
        name: 'Machine X',
        description: null,
        machine_park_id: null,
        yaw: 267.24,
        pitch: 12.05,
      },
      {
        id: 12,
        name: 'Streamline Pro',
        description: null,
        machine_park_id: null,
        yaw: 88.24,
        pitch: 3.85,
      },
    ],
  },
  {
    id: 13,
    factory_id: AAE_F,
    name: 'Laboratory - 2',
    panorama_url: aaePanoramaUrl(13),
    links: [
      {
        id: 30,
        name: 'Laboratory Entry',
        node_id: 11,
        yaw: 283.16,
        pitch: -5.51,
      },
      {
        id: 31,
        name: 'Laboratory - 1',
        node_id: 12,
        yaw: 249.37,
        pitch: -14.81,
      },
    ],
    markers: [
      {
        id: 13,
        name: 'Machine X',
        description: null,
        machine_park_id: null,
        yaw: 198.09,
        pitch: 9.8,
      },
      {
        id: 14,
        name: 'Streamline Pro',
        description: null,
        machine_park_id: null,
        yaw: 88.63,
        pitch: 6.67,
      },
      {
        id: 15,
        name: 'Machine X',
        description: null,
        machine_park_id: null,
        yaw: 340.35,
        pitch: 10.8,
      },
      {
        id: 16,
        name: 'Machine X',
        description: null,
        machine_park_id: null,
        yaw: 260,
        pitch: 4.77,
      },
    ],
  },
  {
    id: 14,
    factory_id: AAE_F,
    name: 'Warehouse 2 - Entry',
    panorama_url: aaePanoramaUrl(14),
    links: [
      { id: 32, name: 'Laboratory Entry', node_id: 11, yaw: 93.19, pitch: 2.3 },
      {
        id: 33,
        name: 'Warehouse 2 - X Area',
        node_id: 15,
        yaw: 359.21,
        pitch: 2.55,
      },
    ],
    markers: [],
  },
  {
    id: 15,
    factory_id: AAE_F,
    name: 'Warehouse 2 - X Area',
    panorama_url: aaePanoramaUrl(15),
    links: [
      {
        id: 34,
        name: 'Warehouse 2 - Entry',
        node_id: 14,
        yaw: 177.85,
        pitch: -0.14,
      },
      {
        id: 35,
        name: 'Warehouse 2 - X Area (2)',
        node_id: 16,
        yaw: 259.52,
        pitch: -0.29,
      },
      {
        id: 36,
        name: 'Warehouse 2 - X Area (5)',
        node_id: 17,
        yaw: 358.49,
        pitch: 1.66,
      },
    ],
    markers: [],
  },
  {
    id: 16,
    factory_id: AAE_F,
    name: 'Warehouse 2 - X Area (2)',
    panorama_url: aaePanoramaUrl(16),
    links: [
      {
        id: 37,
        name: 'Warehouse 2 - X Area',
        node_id: 15,
        yaw: 90.07,
        pitch: 1.5,
      },
    ],
    markers: [],
  },
  {
    id: 17,
    factory_id: AAE_F,
    name: 'Warehouse 2 - X Area (5)',
    panorama_url: aaePanoramaUrl(17),
    links: [
      {
        id: 38,
        name: 'Warehouse 2 - X Area',
        node_id: 15,
        yaw: 88.68,
        pitch: 1.09,
      },
    ],
    markers: [],
  },
  {
    id: 18,
    factory_id: AAE_F,
    name: 'Warehouse 3 - Entry',
    panorama_url: aaePanoramaUrl(18),
    links: [
      {
        id: 39,
        name: 'Front Office Entry',
        node_id: 1,
        yaw: 93.97,
        pitch: 1.72,
      },
      {
        id: 40,
        name: 'Warehouse 3 (2)',
        node_id: 19,
        yaw: 358.44,
        pitch: 2.84,
      },
    ],
    markers: [],
  },
  {
    id: 19,
    factory_id: AAE_F,
    name: 'Warehouse 3 (2)',
    panorama_url: aaePanoramaUrl(19),
    links: [
      {
        id: 41,
        name: 'Warehouse 3 - Entry',
        node_id: 18,
        yaw: 104.37,
        pitch: 0.2,
      },
      {
        id: 42,
        name: 'Warehouse 3 (3)',
        node_id: 20,
        yaw: 268.44,
        pitch: -1.39,
      },
    ],
    markers: [],
  },
  {
    id: 20,
    factory_id: AAE_F,
    name: 'Warehouse 3 (3)',
    panorama_url: aaePanoramaUrl(20),
    links: [
      {
        id: 43,
        name: 'Warehouse 3 - Entry',
        node_id: 18,
        yaw: 189.91,
        pitch: 0.4,
      },
      { id: 44, name: 'Warehouse 3 (2)', node_id: 19, yaw: 88.94, pitch: 0.37 },
    ],
    markers: [],
  },
];

/** Number of panoramic nodes in the AAE showcase walkthrough. */
export const MOCK_AAE_TOUR_NODE_COUNT = AAE_TOUR_NODES.length;

/**
 * Returns the Advanced Armour Engineering (AAE) virtual-tour graph — 20
 * panoramic nodes across the front office, Warehouses 1–3, and the
 * laboratory. Nodes are bound to {@link MOCK_AAE_FACTORY_ID}. Panoramas
 * load from {@link MOCK_AAE_CDN}. Used by MSW, factory edit/view, and
 * the mocked e2e suite.
 */
export function createAAEVirtualTour(): VirtualTourNode[] {
  return AAE_TOUR_NODES;
}

/**
 * Builds a small connected {@link VirtualTourNode} graph
 * (entrance → workshop → QA station) with links and markers, used by the
 * viewer specs, the demo build, and the mocked e2e suite.
 */
export function createMockVirtualTour(): VirtualTourNode[] {
  return [
    {
      ...createMockPsvNode({
        id: 1,
        name: 'Entrance',
        panorama_url: DEMO_PANORAMAS[0],
      }),
      links: [
        createMockPsvLink({ id: 1, name: 'Workshop', node_id: 2, yaw: 40 }),
      ],
      markers: [
        createMockPsvMarker({
          id: 1,
          name: 'Reception',
          description: 'Visitor check-in and safety briefing',
          yaw: 10,
          pitch: -5,
        }),
      ],
    },
    {
      ...createMockPsvNode({
        id: 2,
        name: 'Workshop',
        panorama_url: DEMO_PANORAMAS[1],
      }),
      links: [
        createMockPsvLink({ id: 2, name: 'Entrance', node_id: 1, yaw: 220 }),
        createMockPsvLink({ id: 3, name: 'QA station', node_id: 3, yaw: 90 }),
      ],
      markers: [
        createMockPsvMarker({
          id: 2,
          name: '5-axis CNC mill',
          description: 'DMG MORI, 2021 — tolerance ±0.01 mm',
          machine_park_id: 1,
          yaw: 0,
          pitch: 0,
        }),
      ],
    },
    {
      ...createMockPsvNode({
        id: 3,
        name: 'QA station',
        panorama_url: DEMO_PANORAMAS[2],
      }),
      links: [
        createMockPsvLink({ id: 4, name: 'Workshop', node_id: 2, yaw: 270 }),
      ],
      markers: [
        createMockPsvMarker({
          id: 3,
          name: 'CMM inspection',
          description:
            'Coordinate measuring machine for first-article inspection',
          yaw: 30,
          pitch: -10,
        }),
      ],
    },
  ];
}
